/**
 * TanStack Query hooks for `advertiser-notifications`. Thin wrappers over ./api.
 */
import {
	useMutation,
	useQuery,
	useQueryClient,
	type UseMutationResult,
	type UseQueryResult,
} from "@tanstack/react-query";
import {
	listSubscriptions,
	listFeed,
	createSubscription,
	updateSubscription,
	deleteSubscription,
	markEventRead,
	emitEvent,
	advertiserNotificationsQueryKeys,
	type CreateSubscriptionBody,
	type UpdateSubscriptionBody,
	type EmitEventBody,
	type ListSubscriptionsResponse,
	type ListEventsResponse,
	type SubscriptionRow,
	type EventRow,
} from "./api";

export function useSubscriptionsList(
	advertiserId: string,
	enabled: boolean = true,
): UseQueryResult<ListSubscriptionsResponse, Error> {
	return useQuery({
		queryKey: advertiserNotificationsQueryKeys.subscriptions(advertiserId),
		queryFn: () => listSubscriptions(advertiserId),
		enabled: enabled && advertiserId.length > 0,
	});
}

export function useFeedList(
	advertiserId: string,
	limit?: number,
	enabled: boolean = true,
): UseQueryResult<ListEventsResponse, Error> {
	return useQuery({
		queryKey: advertiserNotificationsQueryKeys.feed(advertiserId, limit),
		queryFn: () => listFeed(advertiserId, limit),
		enabled: enabled && advertiserId.length > 0,
	});
}

export function useCreateSubscription(): UseMutationResult<
	{ subscription: SubscriptionRow },
	Error,
	CreateSubscriptionBody
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: createSubscription,
		onSuccess: () => {
			qc.invalidateQueries({
				queryKey: advertiserNotificationsQueryKeys.all,
			});
		},
	});
}

export function useUpdateSubscription(): UseMutationResult<
	{ subscription: SubscriptionRow },
	Error,
	{ id: string; patch: UpdateSubscriptionBody }
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({ id, patch }) => updateSubscription(id, patch),
		onSuccess: () => {
			qc.invalidateQueries({
				queryKey: advertiserNotificationsQueryKeys.all,
			});
		},
	});
}

export function useDeleteSubscription(): UseMutationResult<
	{ ok: boolean; id: string },
	Error,
	string
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: deleteSubscription,
		onSuccess: () => {
			qc.invalidateQueries({
				queryKey: advertiserNotificationsQueryKeys.all,
			});
		},
	});
}

export function useMarkEventRead(): UseMutationResult<
	{ event: EventRow },
	Error,
	string
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: markEventRead,
		onSuccess: () => {
			qc.invalidateQueries({
				queryKey: advertiserNotificationsQueryKeys.all,
			});
		},
	});
}

export function useEmitEvent(): UseMutationResult<
	{ event: EventRow; delivered_to: number },
	Error,
	EmitEventBody
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: emitEvent,
		onSuccess: () => {
			qc.invalidateQueries({
				queryKey: advertiserNotificationsQueryKeys.all,
			});
		},
	});
}
